import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  formatMinutesAsHours, 
  calculateEfficiency, 
  getEfficiencyColor 
} from '@/utils/payrollDateUtils';
import { PayrollSummaryRow } from '@/hooks/usePayrollSummary';
import { AlertCircle, Clock, FileWarning } from 'lucide-react';

interface PayrollTableProps {
  data: PayrollSummaryRow[];
  loading?: boolean;
  onRowClick?: (row: PayrollSummaryRow) => void;
  compareData?: PayrollSummaryRow[];
}

export function PayrollTable({ data, loading, onRowClick, compareData }: PayrollTableProps) {
  if (loading) {
    return (
      <div className="border rounded-lg p-8 text-center text-muted-foreground">
        Loading payroll data...
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center text-muted-foreground">
        No payroll data found for the selected period.
      </div>
    );
  }

  // Create a map of comparison data by employee_id for quick lookup
  const compareMap = new Map(
    compareData?.map(row => [row.employee_id, row]) || []
  );

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Employee</TableHead>
            <TableHead>Role</TableHead>
            <TableHead className="text-right">Clock Hours</TableHead>
            <TableHead className="text-right">Produced Hours</TableHead>
            <TableHead className="text-right">Efficiency</TableHead>
            <TableHead className="text-right">After-Hours</TableHead>
            <TableHead>Flags</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map(row => {
            const efficiency = row.employee_type === 'technician' 
              ? calculateEfficiency(row.produced_minutes, row.clock_minutes)
              : null;
            
            const compareRow = compareMap.get(row.employee_id);
            const clockDelta = compareRow && compareRow.clock_minutes > 0
              ? ((row.clock_minutes - compareRow.clock_minutes) / compareRow.clock_minutes * 100).toFixed(0)
              : null;
            const producedDelta = compareRow && compareRow.produced_minutes > 0
              ? ((row.produced_minutes - compareRow.produced_minutes) / compareRow.produced_minutes * 100).toFixed(0)
              : null;

            // Warning flags
            const flags: React.ReactNode[] = [];
            
            if (row.produced_minutes > 0 && row.clock_minutes === 0) {
              flags.push(
                <Badge key="no-clock" variant="outline" className="text-orange-600 border-orange-300 gap-1">
                  <AlertCircle className="h-3 w-3" />
                  No clock entries
                </Badge>
              );
            }
            
            if (row.has_unreviewed_entries) {
              flags.push(
                <Badge key="unreviewed" variant="outline" className="text-yellow-600 border-yellow-300 gap-1">
                  <FileWarning className="h-3 w-3" />
                  Unreviewed entries
                </Badge>
              );
            }

            return (
              <TableRow 
                key={row.employee_id} 
                className={onRowClick ? 'cursor-pointer hover:bg-muted/50' : ''}
                onClick={() => onRowClick?.(row)}
              >
                <TableCell className="font-medium">{row.employee_name}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {row.role_label}
                    {row.employee_type === 'technician' && (
                      <Badge variant="secondary" className="text-xs">Tech</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div>{formatMinutesAsHours(row.clock_minutes)}</div>
                  {clockDelta && (
                    <div className={`text-xs ${Number(clockDelta) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {Number(clockDelta) >= 0 ? '+' : ''}{clockDelta}%
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {row.employee_type === 'technician' ? (
                    <>
                      <div>{formatMinutesAsHours(row.produced_minutes)}</div>
                      {producedDelta && (
                        <div className={`text-xs ${Number(producedDelta) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {Number(producedDelta) >= 0 ? '+' : ''}{producedDelta}%
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className={`text-right ${getEfficiencyColor(efficiency)}`}>
                  {efficiency !== null ? `${efficiency}%` : '—'}
                </TableCell>
                <TableCell className="text-right">
                  {formatMinutesAsHours(row.after_hours_minutes)}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {flags.length > 0 ? flags : <span className="text-muted-foreground text-sm">—</span>}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
